import FAQItems from "../components/FAQItems";

const TEXT = "#EDEDED";
const TEXT_DIM = "#a0a0a0";
const TEAL = "#4FCDC4";
const TEAL_DIM = "rgba(79,205,196,0.15)";
const CARD_BORDER = "rgba(255,255,255,0.04)";

function H2({ id, children }) {
  return <h2 id={id} style={{ fontFamily: "var(--display)", fontWeight: 700, fontSize: "clamp(22px, 3vw, 28px)", color: TEXT, margin: "48px 0 16px", letterSpacing: "-0.01em", lineHeight: 1.2 }}>{children}</h2>;
}
function H3({ children }) {
  return <h3 style={{ fontFamily: "var(--display)", fontWeight: 700, fontSize: 19, color: TEXT, margin: "32px 0 12px", letterSpacing: "-0.005em", lineHeight: 1.3 }}>{children}</h3>;
}
function P({ children }) {
  return <p style={{ fontFamily: "var(--body)", fontSize: 16, color: TEXT_DIM, lineHeight: 1.85, margin: "0 0 18px" }}>{children}</p>;
}
function Li({ children }) {
  return <li style={{ fontFamily: "var(--body)", fontSize: 16, color: TEXT_DIM, lineHeight: 1.8, marginBottom: 8 }}>{children}</li>;
}
function Strong({ children }) {
  return <strong style={{ color: TEXT, fontWeight: 700 }}>{children}</strong>;
}
function A({ href, children, external }) {
  const isExternal = external || href.startsWith("http");
  return <a href={href} {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})} style={{ color: TEAL, textDecoration: "underline", fontWeight: 600 }}>{children}</a>;
}
function Code({ children }) {
  return <code style={{ fontFamily: "var(--mono)", fontSize: 14, color: TEXT, background: "rgba(255,255,255,0.06)", padding: "2px 6px", borderRadius: 4 }}>{children}</code>;
}
function PullQuote({ children }) {
  return (
    <blockquote style={{ margin: "32px 0", padding: "20px 24px", borderLeft: `3px solid ${TEAL}`, fontFamily: "var(--display)", fontSize: 20, fontWeight: 600, color: TEXT, lineHeight: 1.4, fontStyle: "normal" }}>
      {children}
    </blockquote>
  );
}

/* ── Visual 1: the 5-step flow ── */
const STEPS = [
  { n: 1, t: "Confirm your site is eligible", d: "Domain and subdomain level only. Open your source-preference screen with the deeplink to check that your site appears." },
  { n: 2, t: "Get your preferred-source deeplink", d: "google.com/preferences/source?q=your-url, the link that takes a reader straight to the screen where they add you." },
  { n: 3, t: "Add the official button", d: "Google's “Add as a preferred source” assets, available in 15 languages, placed like a follow or subscribe button." },
  { n: 4, t: "Prompt the audience you already have", d: "Put the link and button where your readers already engage: newsletter, email signature, social, on-site, post-purchase, events." },
  { n: 5, t: "Be worth choosing", d: "Selection compounds with trust. Keep publishing useful, current, on-topic work so the choice pays off and sticks." },
];
function StepFlow() {
  return (
    <div style={{ background: "#0f0f11", borderRadius: 12, border: `1px solid ${CARD_BORDER}`, padding: 24, margin: "16px 0 32px" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: TEXT, marginBottom: 4, fontFamily: "var(--body)" }}>The 5-step playbook</div>
      <div style={{ fontSize: 10, color: TEXT_DIM, marginBottom: 20, fontFamily: "var(--body)" }}>You cannot add yourself. Steps 1 to 3 make you addable; steps 4 and 5 get you added.</div>
      {STEPS.map((s) => (
        <div key={s.n} style={{ display: "grid", gridTemplateColumns: "32px 1fr", gap: 14, alignItems: "start", padding: "12px 0", borderTop: `1px solid ${CARD_BORDER}` }}>
          <span style={{ width: 28, height: 28, borderRadius: 999, background: TEAL_DIM, color: TEAL, fontFamily: "var(--mono)", fontSize: 14, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{s.n}</span>
          <div>
            <div style={{ fontFamily: "var(--display)", fontSize: 15, fontWeight: 700, color: TEXT, marginBottom: 4 }}>{s.t}</div>
            <div style={{ fontSize: 13, color: TEXT_DIM, lineHeight: 1.55 }}>{s.d}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Visual 2: deeplink + button anatomy ── */
function DeeplinkAnatomy() {
  return (
    <div style={{ background: "#0f0f11", borderRadius: 12, border: `1px solid ${CARD_BORDER}`, padding: 24, margin: "16px 0 32px" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: TEXT, marginBottom: 16, fontFamily: "var(--body)" }}>Your two publisher assets</div>
      <div style={{ fontSize: 11, color: TEXT_DIM, marginBottom: 6, fontFamily: "var(--body)" }}>1. The deeplink (swap in your own domain)</div>
      <div className="blog-scroll-wrap" style={{ overflowX: "auto", marginBottom: 20 }}>
        <code style={{ fontFamily: "var(--mono)", fontSize: 14, color: TEXT, whiteSpace: "nowrap" }}>
          https://google.com/preferences/source?q=<span style={{ color: TEAL, background: TEAL_DIM, padding: "1px 5px", borderRadius: 4 }}>example.com</span>
        </code>
      </div>
      <div style={{ fontSize: 11, color: TEXT_DIM, marginBottom: 8, fontFamily: "var(--body)" }}>2. The official button (15 languages)</div>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 16px", borderRadius: 10, background: "rgba(255,255,255,0.04)", border: `1px solid ${CARD_BORDER}` }}>
        <span style={{ color: TEAL, fontSize: 16, fontWeight: 700 }}>+</span>
        <span style={{ fontFamily: "var(--body)", fontSize: 14, color: TEXT, fontWeight: 600 }}>Add as a preferred source on Google</span>
      </div>
    </div>
  );
}

/* ── Visual 3: where to prompt your audience ── */
function AudienceChannels() {
  const channels = [
    "Newsletter footer plus one dedicated send",
    "Email signature, yours and the team's",
    "Social posts and a pinned profile link",
    "On-site: blog footer, about page, resource hub",
    "Post-purchase and onboarding emails",
    "Event slides, booth screens, and follow-ups",
  ];
  return (
    <div style={{ background: "#0f0f11", borderRadius: 12, border: `1px solid ${CARD_BORDER}`, padding: 24, margin: "16px 0 32px" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: TEXT, marginBottom: 4, fontFamily: "var(--body)" }}>Where to place the prompt</div>
      <div style={{ fontSize: 10, color: TEXT_DIM, marginBottom: 16, fontFamily: "var(--body)" }}>The same channels you use to ask for a follow or a subscribe.</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10 }}>
        {channels.map((c) => (
          <div key={c} style={{ display: "flex", gap: 9, alignItems: "start", padding: "10px 12px", borderRadius: 10, background: "rgba(255,255,255,0.03)", border: `1px solid ${CARD_BORDER}` }}>
            <span style={{ color: TEAL, fontSize: 14, fontWeight: 700, lineHeight: 1.5 }}>✓</span>
            <span style={{ fontSize: 13, color: TEXT_DIM, lineHeight: 1.5 }}>{c}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const faqs = [
  {
    q: "Can I add my own website as a Google preferred source?",
    a: "No. A site cannot select itself; only a reader can choose your site as a preferred source. What you can do as a publisher is make it easy for your audience to select you, using the deeplink and button Google provides. Google states explicitly that these promotion methods are not required to be eligible, so prompting your audience is a growth tactic, not a prerequisite.",
  },
  {
    q: "How do I get the deeplink to become a preferred source?",
    a: "Google provides a deeplink in the format https://google.com/preferences/source?q=your-url. Swap in your own domain, for example https://google.com/preferences/source?q=example.com, and the link takes a reader straight to the screen where they can add your site. You place that link wherever you already ask your audience to follow or subscribe.",
  },
  {
    q: "Do I have to prompt my audience to be eligible?",
    a: "No. Eligibility depends only on the domain or subdomain rule, not on whether you promote yourself. The deeplink and button are optional aids that lower friction for the audience you already have. Prompting does not make you eligible; it increases how many eligible readers actually select you.",
  },
  {
    q: "Is my whole site eligible or just my blog?",
    a: "Only domain-level and subdomain-level URLs are eligible to appear in the source preferences tool. A subdirectory such as example.com/blog cannot be selected on its own; readers select the whole domain (example.com) or a subdomain (blog.example.com). If your content lives on a subdirectory of a domain you do not control, you cannot be selected, which is a reason to publish on a property you own.",
  },
  {
    q: "How long does it take to become a preferred source?",
    a: "There is no application or approval queue. If your site is eligible, it can be selected by a reader immediately through the deeplink or the in-Search tool. The slower part is not eligibility; it is building the audience demand to select you. Treat it like growing newsletter subscribers: the mechanism is instant, the momentum compounds over weeks.",
  },
  {
    q: "How do I know if people have made me a preferred source?",
    a: "Google does not give publishers a direct count of how many users have selected them. You cannot see the number, so measure it indirectly: track your visibility and citations across Top Stories, AI Overviews, and AI Mode over time, and watch for lift among the audience segments you prompted. AI visibility tracking tools, including AI-Advisors, monitor those AI surfaces so you can infer movement even without a first-party selection count.",
  },
  {
    q: "Is becoming a Google preferred source worth it for B2B?",
    a: "Yes, when you have an engaged audience. The payoff is that the people who already trust you, your subscribers, customers, and community, will see your brand surfaced with a preferred badge in their Google AI answers when they research your category. For B2B brands with a newsletter or a customer base, it converts owned-audience trust into AI search visibility at no media cost. For brands with no audience to prompt yet, the eligibility groundwork still matters, but the lever is weaker until you build that audience.",
  },
];

function Content() {
  return (
    <article style={{ maxWidth: 720, margin: "0 auto" }}>
      {/* Overview box */}
      <div style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${CARD_BORDER}`, borderRadius: 12, padding: "20px 24px", marginBottom: 32 }} id="quick-answer">
        <P>
          <Strong>You cannot make your own site a Google preferred source. Only your readers can.</Strong> So &ldquo;becoming&rdquo; one is really two jobs: make your site eligible, then prompt the audience you already have to select you. Once they do, your content surfaces more often, with a <A href="/blog/what-is-google-preferred-sources">&ldquo;preferred&rdquo; badge</A>, in Top Stories, AI Overviews, and AI Mode for those readers. This is the B2B playbook.
        </P>
        <ul style={{ margin: "12px 0 0", paddingLeft: 20 }}>
          <Li><Strong>Step 1:</Strong> confirm eligibility (domain or subdomain, not a subdirectory).</Li>
          <Li><Strong>Step 2:</Strong> get your deeplink, <Code>google.com/preferences/source?q=your-url</Code>.</Li>
          <Li><Strong>Step 3:</Strong> add Google&#39;s official button (15 languages).</Li>
          <Li><Strong>Step 4:</Strong> prompt your audience where they already engage.</Li>
          <Li><Strong>Step 5:</Strong> keep being worth choosing.</Li>
        </ul>
      </div>

      {/* §0 reframe */}
      <H2 id="can-you-make-your-site-a-preferred-source">Can you make your own site a preferred source?</H2>
      <P>
        Start with the reframe that changes the whole approach: there is no button on your end that adds your site, no form to submit, and no approval queue. A preferred source is chosen by a reader, not claimed by a publisher. Per <A href="https://developers.google.com/search/docs/appearance/preferred-sources" external>Google&#39;s Search Central documentation</A>, the deeplink and button you can use to encourage selections are explicitly <Strong>not required</Strong> to be eligible.
      </P>
      <P>
        So &ldquo;how to become a preferred source&rdquo; splits into two jobs. The first is technical and one-time: make sure your site is eligible and easy to select. The second is ongoing and is where the real work lives: get the audience you already have to actually select you. The five steps below cover both, in order.
      </P>

      <StepFlow />

      {/* §1 */}
      <H2 id="step-1-eligibility">Step 1: Confirm your site is eligible</H2>
      <P>
        Eligibility is a precise technical rule: only <Strong>domain-level and subdomain-level</Strong> sites can appear in the source preferences tool. A subdirectory like <Code>example.com/blog</Code> is not eligible on its own; readers select the whole domain (<Code>example.com</Code>) or a subdomain (<Code>blog.example.com</Code>).
      </P>
      <P>
        To check, open your own source-preference screen using the deeplink in Step 2 with your domain. If your site lives on a subdirectory of a domain you do not control, such as a profile on a third-party platform, you cannot be selected, and the fix is to publish on a property you own. For most B2B brands on their own domain or blog subdomain, you are already eligible and can move on.
      </P>

      {/* §2 */}
      <H2 id="step-2-deeplink">Step 2: Get your preferred-source deeplink</H2>
      <P>
        Google supplies a deeplink that sends a reader straight to the screen where they add your site. The format is:
      </P>
      <div className="blog-scroll-wrap" style={{ overflowX: "auto", margin: "0 0 18px" }}>
        <code style={{ fontFamily: "var(--mono)", fontSize: 14, color: TEXT, whiteSpace: "nowrap" }}>https://google.com/preferences/source?q=your-url</code>
      </div>
      <P>
        Swap <Code>your-url</Code> for your domain, for example <Code>https://google.com/preferences/source?q=example.com</Code>. This single link is the most important asset in this playbook. Anywhere you can put a link, you can ask your audience to make you a preferred source in one tap.
      </P>

      {/* §3 */}
      <H2 id="step-3-button">Step 3: Add the official button</H2>
      <P>
        Google also provides downloadable button assets that read &ldquo;Add as a preferred source on Google,&rdquo; available in 15 languages plus an all-languages bundle. Use the button the same way you use a follow or subscribe button: a recognizable, low-friction prompt placed alongside your other calls to action.
      </P>

      <DeeplinkAnatomy />

      {/* §4 */}
      <H2 id="step-4-prompt-audience">Step 4: Prompt the audience you already have</H2>
      <P>
        This is the step that actually moves the needle, and it is pure B2B distribution. You are not buying placement and you are not optimizing an algorithm. You are asking the people who already trust you to tell Google they want more of you. Your newsletter subscribers, customers, community members, and event attendees are exactly the readers most likely to say yes.
      </P>
      <P>
        Treat it like any audience ask. Put the deeplink and button where your readers already engage, lead with the benefit (they get more of the source they already follow, surfaced in their Google AI answers), and ask more than once.
      </P>

      <AudienceChannels />

      <PullQuote>
        You are not optimizing an algorithm. You are asking the people who already trust you to vote for you.
      </PullQuote>

      {/* §5 */}
      <H2 id="step-5-be-worth-choosing">Step 5: Be worth choosing</H2>
      <P>
        A selection is only as valuable as the content behind it. This step is general answer engine optimization, not a Google-specified preferred-source rule: keep publishing useful, accurate, current, on-topic work so that being your reader&#39;s preferred source actually pays off and the choice sticks. The same fundamentals that earn you <A href="/blog/how-to-get-cited-by-google-ai-overviews">organic citations in AI Overviews</A>, clear answers, real expertise, and a steady freshness cadence, make a preferred-source selection worth keeping.
      </P>

      {/* §6 mistakes */}
      <H2 id="common-mistakes">Common mistakes to avoid</H2>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><Strong>Waiting to be added by Google.</Strong> There is no approval step. If you are eligible, the only thing between you and selections is asking your audience.</Li>
        <Li><Strong>Promoting a subdirectory.</Strong> Readers select a domain or subdomain, never a subfolder. Point the deeplink at the property you own.</Li>
        <Li><Strong>Treating it as pay-to-play.</Strong> There is no spend and no auction. The currency is audience trust.</Li>
        <Li><Strong>Asking once.</Strong> Like any subscribe ask, selections compound when the prompt is persistent and placed across channels.</Li>
        <Li><Strong>Expecting a dashboard of who selected you.</Strong> Google does not provide a selection count. Measure indirectly, as below.</Li>
      </ul>

      {/* §7 measure */}
      <H2 id="how-to-measure">How to know it is working</H2>
      <P>
        Be honest about the limits here: <Strong>Google does not give publishers a count of how many users have made them a preferred source.</Strong> You will not see that number. So measure the outcome it is supposed to produce instead. Track your presence and citations across Top Stories, AI Overviews, and AI Mode over time, and watch for lift concentrated in the audience segments you prompted.
      </P>
      <P>
        This is where AI visibility tracking earns its place. A tool that monitors your citations and mentions across Google&#39;s AI surfaces, including <A href="/platform/aeo">the AI-Advisors platform</A>, lets you infer whether your preferred-source push is translating into more AI-answer visibility, even without a first-party selection count. Pair it with the simplest signal of all: ask a few engaged readers whether they selected you, and whether they notice your badge.
      </P>

      {/* HowTo + FAQPage JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "HowTo",
        "name": "How to Become a Google Preferred Source",
        "description": "A B2B playbook to make your site eligible as a Google preferred source and prompt your audience to select you, so your content surfaces with a preferred badge in Top Stories, AI Overviews, and AI Mode.",
        "step": STEPS.map((s) => ({
          "@type": "HowToStep",
          "position": s.n,
          "name": s.t,
          "text": s.d,
        })),
      }) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map((faq) => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": { "@type": "Answer", "text": faq.a },
        })),
      }) }} />

      {/* FAQ */}
      <H2 id="faq">Frequently Asked Questions</H2>
      <FAQItems faqs={faqs} />

      {/* Related Reading */}
      <H2 id="related">Related Reading</H2>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><A href="/blog/what-is-google-preferred-sources">What Is Google Preferred Sources? The New AI Visibility Lever</A></Li>
        <Li><A href="/blog/why-google-preferred-sources-matter">Why Google Preferred Sources Is Your Newest AI-Visibility Lever</A></Li>
        <Li><A href="/blog/how-to-get-cited-by-google-ai-overviews">How to Get Cited by Google AI Overviews</A></Li>
        <Li><A href="/blog/google-ai-mode-vs-ai-overviews">Google AI Mode vs Google AI Overviews: What&#39;s the Difference?</A></Li>
        <Li><A href="/blog/how-to-optimize-website-for-ai-search">How to Optimize a Website for AI Search (2026)</A></Li>
      </ul>
    </article>
  );
}

const post = {
  slug: "how-to-become-a-google-preferred-source",
  title: "How to Become a Google Preferred Source: A B2B Playbook",
  seoTitle: "How to Become a Google Preferred Source (2026)",
  excerpt: "You cannot make your own site a Google preferred source; only your readers can. Here is the B2B playbook to confirm eligibility, get your deeplink and button, and prompt the audience you already have to choose you in Google's AI answers.",
  metaDescription: "You cannot add your own site as a Google preferred source; readers select you. The B2B playbook to become eligible and earn those selections.",
  date: "2026-06-04",
  readTime: "8 min read",
  category: "Answer Engine Optimization",
  tags: ["Google Preferred Sources", "AI visibility", "Google AI Overviews", "AI Mode", "answer engine optimization", "B2B marketing"],
  Content,
};

export default post;
